import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardData } from '../../services/api';
import { CardContainer } from '../CardContainer';

interface PendingPaymentTableProps {
  payments: DashboardData['pending_payment_list'];
  /**
   * Clinic-wide totals across every pending sale. `payments` is capped by the backend and
   * paginated here, so these are the only reliable aggregates.
   */
  summary?: DashboardData['pending_payments'];
}

const ITEMS_PER_PAGE = 5;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatCurrency = (amount: number) => {
  return `Q${amount.toFixed(2)}`;
};

export const PendingPaymentTable = ({ payments, summary }: PendingPaymentTableProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(payments.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const paginatedPayments = payments.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleCollect = (visitId: number) => {
    navigate(`/commercial-handoff/${visitId}`);
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (payments.length === 0) {
    return (
      <CardContainer>
        <h3 className="text-lg font-semibold text-[#101822] mb-4">Pagos Pendientes</h3>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-50 text-green-600">
            <span className="material-symbols-outlined text-xl block">check_circle</span>
          </div>
          <div>
            <p className="text-sm font-medium text-[#101822]">Todo cobrado</p>
            <p className="text-sm text-slate-500">No hay saldos por cobrar.</p>
          </div>
        </div>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <div className="flex items-baseline justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-[#101822]">Pagos Pendientes</h3>
        {summary && (
          <div className="text-right shrink-0">
            <p className="text-lg font-semibold text-orange-600 leading-tight">
              {formatCurrency(summary.total_amount)}
            </p>
            <p className="text-xs text-slate-500">
              {summary.count} {summary.count === 1 ? 'venta' : 'ventas'} · todas las fechas
            </p>
          </div>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3">
                Paciente
              </th>
              <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 hidden sm:table-cell">
                Fecha
              </th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider pb-3">
                Pendiente
              </th>
              <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider pb-3 w-20">

              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {paginatedPayments.map((payment) => (
              <tr key={payment.visit_id} className="hover:bg-slate-50">
                <td className="py-3">
                  <p className="text-sm font-medium text-[#101822] truncate max-w-[150px]">
                    {payment.patient_name}
                  </p>
                  <p className="text-xs text-slate-400 sm:hidden">{formatDate(payment.visit_date)}</p>
                </td>
                <td className="py-3 hidden sm:table-cell">
                  <p className="text-sm text-slate-500">{formatDate(payment.visit_date)}</p>
                </td>
                <td className="py-3 text-right">
                  <p className="text-sm font-semibold text-orange-600">
                    {formatCurrency(payment.pending_amount)}
                  </p>
                  <p className="text-xs text-slate-400">
                    de {formatCurrency(payment.grand_total)}
                  </p>
                </td>
                <td className="py-3 pl-4 text-right">
                  <button
                    onClick={() => handleCollect(payment.visit_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Cobrar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              currentPage === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>

          <span className="text-xs text-slate-500">
            {currentPage + 1} / {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              currentPage === totalPages - 1
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </CardContainer>
  );
};
